<template>
  <div class="user-management-container">
    <!-- 移动端用户管理 -->
    <div class="mobile-user-management">
      <div class="navbar">
        <a-icon type="arrow-left" class="navbar-back" @click="handleBack" />
        <h1 class="navbar-title">用户管理</h1>
        <a-icon type="plus" class="navbar-add" @click="handleAddUser" />
      </div>

      <div class="search-section">
        <a-input-search
          v-model="searchKeyword"
          placeholder="搜索邮箱"
          @search="handleSearch"
          class="search-input"
        />
      </div>

      <div class="user-list">
        <div
          v-for="user in userList"
          :key="user.id"
          class="user-item"
        >
          <div class="user-avatar">
            <a-avatar icon="user" />
          </div>
          <div class="user-info">
            <h3 class="user-email">{{ user.email }}</h3>
            <p class="user-id">ID: {{ user.id }}</p>
          </div>
          <div class="user-actions">
            <a-icon type="edit" class="action-icon" @click="handleEditUser(user)" />
            <a-icon type="delete" class="action-icon delete" @click="handleDeleteUser(user)" />
          </div>
        </div>
      </div>

      <div class="pagination-section">
        <a-pagination
          v-model="pagination.current"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          @change="handlePageChange"
          simple
        />
      </div>
    </div>

    <!-- PC端用户管理 -->
    <div class="pc-user-management">
      <div class="sidebar">
        <div class="sidebar-header">
          <div class="logo-icon">✓</div>
          <h1 class="logo-text">TaskFlow</h1>
        </div>
        <div class="sidebar-menu">
          <a-menu :selected-keys="['user-management']" @click="handleMenuClick">
            <a-menu-item key="task-list">
              <a-icon type="appstore" /> 任务管理
            </a-menu-item>
            <a-menu-item key="statistics">
              <a-icon type="bar-chart" /> 数据统计
            </a-menu-item>
            <a-menu-item key="user-management">
              <a-icon type="team" /> 用户管理
            </a-menu-item>
            <a-menu-item key="settings">
              <a-icon type="setting" /> 设置
            </a-menu-item>
          </a-menu>
        </div>
        <div class="sidebar-footer">
          <div class="user-info">
            <a-avatar icon="user" />
            <span class="user-name">{{ currentUser.email }}</span>
          </div>
          <a-button type="text" @click="handleLogout">退出登录</a-button>
        </div>
      </div>

      <div class="main-content">
        <div class="main-header">
          <h2 class="page-title">用户管理</h2>
          <a-button type="primary" @click="handleAddUser">
            <a-icon type="plus" /> 新增用户
          </a-button>
        </div>

        <div class="search-section">
          <a-input-search
            v-model="searchKeyword"
            placeholder="搜索邮箱"
            @search="handleSearch"
            class="search-input"
            style="width: 300px"
          />
        </div>

        <div class="table-section">
          <a-table
            :columns="columns"
            :data-source="userList"
            :pagination="pagination"
            :loading="loading"
            row-key="id"
          >
            <template slot="action" slot-scope="text, record">
              <a-button type="link" @click="handleEditUser(record)">编辑</a-button>
              <a-button type="link" class="delete-btn" @click="handleDeleteUser(record)">删除</a-button>
            </template>
          </a-table>
        </div>
      </div>
    </div>

    <!-- 新增/编辑用户弹窗 -->
    <a-modal
      :title="modalTitle"
      :visible="modalVisible"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      :confirm-loading="modalLoading"
    >
      <a-form :form="form" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-item label="邮箱">
          <a-input
            v-decorator="[
              'email',
              { rules: [{ required: true, message: '请输入邮箱' }, { type: 'email', message: '邮箱格式不正确' }] }
            ]"
            placeholder="请输入邮箱"
          />
        </a-form-item>
        <a-form-item label="密码">
          <a-input-password
            v-decorator="[
              'password',
              { rules: [{ required: !isEdit, message: '请输入密码' }] }
            ]"
            placeholder="请输入密码"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // 当前登录用户
      currentUser: {
        email: ''
      },
      // 搜索关键词
      searchKeyword: '',
      // 用户列表
      userList: [],
      // 表格列配置
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          width: 80
        },
        {
          title: '邮箱',
          dataIndex: 'email',
          key: 'email'
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 200
        }
      ],
      // 分页配置
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      // 加载状态
      loading: false,
      // 弹窗相关
      modalVisible: false,
      modalLoading: false,
      modalTitle: '新增用户',
      isEdit: false,
      editUserId: null,
      form: this.$form.createForm(this)
    };
  },
  mounted() {
    // 获取当前登录用户
    const user = localStorage.getItem('user');
    if (user) {
      this.currentUser = JSON.parse(user);
    }
    // 加载用户列表
    this.loadUserList();
  },
  methods: {
    // 加载用户列表
    loadUserList() {
      this.loading = true;
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        keyword: this.searchKeyword
      };
      this.$http
        .get('/api/users', { params })
        .then(response => {
          if (response.success) {
            this.userList = response.data.list;
            this.pagination.total = response.data.total;
          } else {
            this.$message.error(response.message || '获取用户列表失败');
          }
        })
        .catch(error => {
          console.error('获取用户列表失败:', error);
          this.$message.error('获取用户列表失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 搜索
    handleSearch() {
      this.pagination.current = 1;
      this.loadUserList();
    },
    // 分页变化
    handlePageChange(page) {
      this.pagination.current = page;
      this.loadUserList();
    },
    // 新增用户
    handleAddUser() {
      this.isEdit = false;
      this.editUserId = null;
      this.modalTitle = '新增用户';
      this.modalVisible = true;
      this.form.resetFields();
    },
    // 编辑用户
    handleEditUser(user) {
      this.isEdit = true;
      this.editUserId = user.id;
      this.modalTitle = '编辑用户';
      this.modalVisible = true;
      this.$nextTick(() => {
        this.form.setFieldsValue({
          email: user.email
        });
      });
    },
    // 删除用户
    handleDeleteUser(user) {
      // 不能删除自己
      if (user.id === this.currentUser.id) {
        this.$message.warning('不能删除当前登录用户');
        return;
      }
      this.$confirm({
        title: '确认删除',
        content: `确定要删除用户 "${user.email}" 吗？`,
        onOk: () => {
          this.$http
            .delete(`/api/users/${user.id}`)
            .then(response => {
              if (response.success) {
                this.$message.success('删除成功');
                this.loadUserList();
              } else {
                this.$message.error(response.message || '删除失败');
              }
            })
            .catch(error => {
              console.error('删除失败:', error);
              this.$message.error('删除失败，请稍后重试');
            });
        }
      });
    },
    // 弹窗确认
    handleModalOk() {
      this.form.validateFields((err, values) => {
        if (!err) {
          this.modalLoading = true;
          const url = this.isEdit ? `/api/users/${this.editUserId}` : '/api/users';
          const method = this.isEdit ? 'put' : 'post';
          const data = {
            email: values.email
          };
          if (values.password) {
            data.password = values.password;
          }
          this.$http[method](url, data)
            .then(response => {
              if (response.success) {
                this.$message.success(this.isEdit ? '编辑成功' : '新增成功');
                this.modalVisible = false;
                this.loadUserList();
              } else {
                this.$message.error(response.message || (this.isEdit ? '编辑失败' : '新增失败'));
              }
            })
            .catch(error => {
              console.error(this.isEdit ? '编辑失败:' : '新增失败:', error);
              this.$message.error(this.isEdit ? '编辑失败，请稍后重试' : '新增失败，请稍后重试');
            })
            .finally(() => {
              this.modalLoading = false;
            });
        }
      });
    },
    // 弹窗取消
    handleModalCancel() {
      this.modalVisible = false;
      this.form.resetFields();
    },
    // 菜单点击
    handleMenuClick({ key }) {
      if (key === 'task-list') {
        this.$router.push('/taskflow/task-list');
      } else if (key === 'statistics') {
        this.$router.push('/taskflow/statistics');
      } else if (key === 'user-management') {
        // 当前页面，不跳转
      } else if (key === 'settings') {
        this.$message.info('设置功能开发中');
      }
    },
    // 返回
    handleBack() {
      this.$router.push('/taskflow/task-list');
    },
    // 退出登录
    handleLogout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/taskflow/login');
      this.$message.success('退出登录成功');
    }
  }
};
</script>

<style lang="less">
.user-management-container {
  min-height: 100vh;
  background-color: #f5f5f5;

  // 移动端样式
  .mobile-user-management {
    display: none;
    max-width: 375px;
    margin: 0 auto;
    background-color: #fff;
    min-height: 100vh;

    .navbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      background-color: #4caf50;
      color: #fff;

      .navbar-back,
      .navbar-add {
        font-size: 20px;
        cursor: pointer;
      }

      .navbar-title {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
      }
    }

    .search-section {
      padding: 16px;
      border-bottom: 1px solid #f0f0f0;

      .search-input {
        width: 100%;
      }
    }

    .user-list {
      padding: 0 16px;

      .user-item {
        display: flex;
        align-items: center;
        padding: 16px 0;
        border-bottom: 1px solid #f0f0f0;

        .user-avatar {
          margin-right: 12px;
        }

        .user-info {
          flex: 1;

          .user-email {
            font-size: 16px;
            font-weight: 500;
            color: #1a1a1a;
            margin: 0 0 4px 0;
          }

          .user-id {
            font-size: 12px;
            color: #999;
            margin: 0;
          }
        }

        .user-actions {
          display: flex;
          gap: 16px;

          .action-icon {
            font-size: 18px;
            color: #666;
            cursor: pointer;

            &:hover {
              color: #4caf50;
            }

            &.delete:hover {
              color: #ff4d4f;
            }
          }
        }
      }
    }

    .pagination-section {
      padding: 16px;
      text-align: center;
    }
  }

  // PC端样式
  .pc-user-management {
    display: flex;
    min-height: 100vh;

    .sidebar {
      width: 240px;
      background-color: #fff;
      border-right: 1px solid #f0f0f0;
      display: flex;
      flex-direction: column;

      .sidebar-header {
        padding: 24px;
        display: flex;
        align-items: center;
        border-bottom: 1px solid #f0f0f0;

        .logo-icon {
          width: 40px;
          height: 40px;
          background-color: #4caf50;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 20px;
          font-weight: 700;
          margin-right: 12px;
        }

        .logo-text {
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
      }

      .sidebar-menu {
        flex: 1;
        padding: 16px 0;

        .ant-menu {
          border-right: none;
        }
      }

      .sidebar-footer {
        padding: 16px;
        border-top: 1px solid #f0f0f0;

        .user-info {
          display: flex;
          align-items: center;
          margin-bottom: 12px;

          .user-name {
            margin-left: 8px;
            font-size: 14px;
            color: #1a1a1a;
          }
        }
      }
    }

    .main-content {
      flex: 1;
      padding: 24px;
      overflow: auto;

      .main-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .page-title {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
      }

      .search-section {
        margin-bottom: 24px;
      }

      .table-section {
        background-color: #fff;
        padding: 24px;
        border-radius: 8px;

        .delete-btn {
          color: #ff4d4f;
        }
      }
    }
  }

  // 响应式设计
  @media (max-width: 768px) {
    .mobile-user-management {
      display: block;
    }

    .pc-user-management {
      display: none;
    }
  }
}
</style>
